import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { api, apiRequest } from '../api/client'
import { MeResponseSchema } from '@shared/schema/api'
import type { MeResponse, UserSummary, Role } from '@shared/schema/api'

type SessionState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: UserSummary }
  | { status: 'anonymous' }
  | { status: 'setupRequired' }

type SessionContextValue = {
  state: SessionState
  user: UserSummary | null
  isAdmin: boolean
  refresh: () => Promise<void>
  setSession: (response: MeResponse) => void
  logout: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' })

  const applyResponse = useCallback((response: MeResponse) => {
    if (response.status === 'authenticated') setState({ status: 'authenticated', user: response.user })
    else if (response.status === 'setupRequired') setState({ status: 'setupRequired' })
    else setState({ status: 'anonymous' })
  }, [])

  const refresh = useCallback(async () => {
    try {
      applyResponse(await api.get('/api/auth/me', MeResponseSchema))
    } catch {
      // เชื่อมต่อไม่ได้ก็ยังถือว่าไม่ได้ล็อกอิน แล้วให้ผู้ใช้ลองใหม่
      setState({ status: 'anonymous' })
    }
  }, [applyResponse])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' })
    } finally {
      setState({ status: 'anonymous' })
    }
  }, [])

  const value: SessionContextValue = {
    state,
    user: state.status === 'authenticated' ? state.user : null,
    isAdmin: state.status === 'authenticated' && state.user.role === 'admin',
    refresh,
    setSession: applyResponse,
    logout,
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession ต้องอยู่ภายใน SessionProvider')
  return context
}

export function useRequiredUser(): UserSummary {
  const { user } = useSession()
  if (!user) throw new Error('ต้องล็อกอินก่อน')
  return user
}

export type { Role }
